import api from './api';
import {
  Project,
  CreateProjectData,
  UpdateProjectData,
  AttachConversationData,
  ProjectConversation,
  ProjectArtifact
} from '../types/project.types';

class ProjectService {
  private readonly BASE_PATH = '/api/projects';

  async createProject(data: CreateProjectData): Promise<Project> {
    return await api.post<Project>(this.BASE_PATH, data);
  }

  async getProjects(includeArchived = false): Promise<Project[]> {
    const path = includeArchived ? `${this.BASE_PATH}?includeArchived=true` : this.BASE_PATH;
    return await api.get<Project[]>(path);
  }

  async getProject(projectId: string): Promise<Project> {
    return await api.get<Project>(`${this.BASE_PATH}/${projectId}`);
  }

  async updateProject(projectId: string, data: UpdateProjectData): Promise<Project> {
    return await api.put<Project>(`${this.BASE_PATH}/${projectId}`, data);
  }

  async deleteProject(projectId: string): Promise<void> {
    await api.delete(`${this.BASE_PATH}/${projectId}`);
  }

  async attachConversation(projectId: string, conversationId: string): Promise<void> {
    const data: AttachConversationData = { conversationId };
    await api.post(`${this.BASE_PATH}/${projectId}/conversations`, data);
  }

  async detachConversation(projectId: string, conversationId: string): Promise<void> {
    await api.delete(`${this.BASE_PATH}/${projectId}/conversations/${conversationId}`);
  }

  async getProjectConversations(projectId: string): Promise<ProjectConversation[]> {
    return await api.get<ProjectConversation[]>(
      `${this.BASE_PATH}/${projectId}/conversations`
    );
  }

  async getProjectArtifacts(projectId: string): Promise<ProjectArtifact[]> {
    return await api.get<ProjectArtifact[]>(
      `${this.BASE_PATH}/${projectId}/artifacts`
    );
  }

  async updateArtifact(artifactId: string, data: { name: string }): Promise<ProjectArtifact> {
    return await api.put<ProjectArtifact>(`${this.BASE_PATH}/artifacts/${artifactId}`, data);
  }

  async deleteArtifact(projectId: string, artifactId: string): Promise<void> {
    await api.delete(`${this.BASE_PATH}/${projectId}/artifacts/${artifactId}`);
  }

  async archiveProject(projectId: string): Promise<Project> {
    return this.updateProject(projectId, { isArchived: true });
  }

  async unarchiveProject(projectId: string): Promise<Project> {
    return this.updateProject(projectId, { isArchived: false });
  }
}

const projectService = new ProjectService();
export default projectService;